/**
 * Makes `<video autoplay>` actually autoplay, everywhere in the theme.
 *
 * THE DEFECT THIS FIXES, in the client's words: "sometimes the videos play automatically
 * and in a loop (which is what I would like) but sometimes (far less often but still
 * happens), they do not automatically play and instead load with a play button over them."
 *
 * The markup was never the problem -- every <video> here already carries muted, loop,
 * autoplay and playsinline, which is the complete set a browser requires. The problem is
 * that `autoplay` is a REQUEST, evaluated once, and a browser is free to decline it. When
 * it declines, the element sits on its poster frame with a tap-to-play affordance over it,
 * which is exactly what she photographed. Nothing in the theme was listening for that, so a
 * declined video stayed declined for the rest of the page's life. There was no .play() call
 * anywhere in the theme before this file.
 *
 * There are two independent reasons a browser declines, and the site hit both:
 *
 * 1. THE VIDEO IS NOT ON SCREEN WHEN THE PAGE LOADS. Mobile browsers do not autoplay
 *    off-screen video -- it would burn a phone's battery and data decoding frames nobody is
 *    looking at -- and, critically, they do not reconsider when it later scrolls into view.
 *    On this store the videos sit at the first position of most products, in a vertical feed
 *    of products, each of which is itself a horizontal slide track. Only the one or two
 *    videos inside the initial viewport were ever asked to play. Every other one was
 *    permanently frozen from the moment the page rendered. THIS IS ALSO WHY IT LOOKED
 *    INTERMITTENT: whether a given video worked depended on where she happened to be
 *    scrolled, so the same video would play on one visit and not the next.
 *
 * 2. iOS LOW POWER MODE. Safari suspends autoplay entirely while Low Power Mode is on --
 *    a device policy, and correctly not something a site can override. But the ban is on
 *    AUTOplay, not on playback: a play() issued from inside a user gesture still succeeds.
 *    So this is recoverable too, just not automatically, which is why there is a one-shot
 *    gesture listener below. It was previously going to be reported to the client as
 *    unfixable; it is only unfixable without asking.
 *
 * WHY THE `autoplay` ATTRIBUTE STAYS IN THE MARKUP. This file is an escalation, not a
 * replacement. A browser that is willing to autoplay does so from the attribute alone,
 * before this script has parsed, with no dependency on JS at all -- so the common case is
 * untouched and cannot be made worse by a bug in here. Everything below only ever tries to
 * start a video that the browser already refused.
 *
 * WHY IT PAUSES OFF-SCREEN VIDEO. Same reason the browser wanted to: on a feed this long,
 * leaving every video decoding is a real battery and data cost on the phones this site is
 * mostly viewed on. `loop` is untouched -- an on-screen video still loops forever, which is
 * what she asked for -- and leaving the viewport resumes rather than restarts, so scrolling
 * back does not jerk the clip to zero.
 */
(function () {
  'use strict';

  // Idempotent: two sections load this file, and the theme editor re-injects section
  // markup (and its <script src>) on every settings change. Without this guard the
  // observers stack up and each video accumulates a play/pause listener per re-render.
  if (window.HighThreadVideoAutoplay) return;

  var VIDEO_SELECTOR = 'video[autoplay]';

  // Videos whose play() was rejected. These are the only ones the gesture handler retries,
  // so a video the user has deliberately paused is never yanked back into playing.
  var blocked = [];

  function attempt(video) {
    // Re-asserted rather than assumed. A browser restoring a page from the back/forward
    // cache can hand back an element whose muted state was changed by the user, and an
    // unmuted play() is rejected outright -- the one failure mode that would otherwise
    // survive every retry below.
    video.muted = true;
    video.playsInline = true;

    var promise;
    try {
      promise = video.play();
    } catch (e) {
      remember(video);
      return;
    }
    // Older Safari returns undefined instead of a promise; there is nothing to attach to,
    // and nothing to handle -- it either played or it did not.
    if (!promise || typeof promise.catch !== 'function') return;
    promise.then(function () { forget(video); }, function () { remember(video); });
  }

  function remember(video) {
    if (blocked.indexOf(video) === -1) blocked.push(video);
  }

  function forget(video) {
    var i = blocked.indexOf(video);
    if (i !== -1) blocked.splice(i, 1);
  }

  // rootMargin starts the fetch just before the video is actually visible, so the first
  // frame is ready rather than arriving a beat late; preload="metadata" in the markup means
  // nothing but the header has been fetched until this point.
  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          attempt(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.15 });
  }

  function register(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll(VIDEO_SELECTOR);
    for (var i = 0; i < videos.length; i++) {
      var video = videos[i];
      if (video.dataset.htAutoplayBound) continue;
      video.dataset.htAutoplayBound = '1';
      video.muted = true;
      video.playsInline = true;
      if (observer) {
        observer.observe(video);
      } else {
        // No IntersectionObserver (very old browsers): fall back to simply asking. That is
        // no worse than the markup-only behaviour this file replaces.
        attempt(video);
      }
    }
  }

  /**
   * The Low Power Mode rescue. One gesture anywhere on the page lifts the autoplay ban for
   * the rest of the visit, so this only has to fire once -- after that the observer handles
   * everything, including videos scrolled to later.
   *
   * Deliberately NOT { once: true } on each listener: `once` would spend the handler on
   * whichever event fires first, and a single tap fires pointerdown AND touchstart AND
   * click. Removing all three together is what actually makes it one-shot.
   */
  function onFirstGesture() {
    ['pointerdown', 'touchstart', 'click', 'keydown'].forEach(function (type) {
      document.removeEventListener(type, onFirstGesture, true);
    });
    // Copy first: attempt() mutates `blocked` through its promise handlers.
    blocked.slice().forEach(function (video) {
      if (isOnScreen(video)) attempt(video);
    });
  }

  function isOnScreen(video) {
    var r = video.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    return r.bottom > 0 && r.right > 0 &&
      r.top < (window.innerHeight || document.documentElement.clientHeight) &&
      r.left < (window.innerWidth || document.documentElement.clientWidth);
  }

  ['pointerdown', 'touchstart', 'click', 'keydown'].forEach(function (type) {
    document.addEventListener(type, onFirstGesture, true);
  });

  // Returning to the tab after it was backgrounded is the other moment a paused-by-policy
  // video can be restarted without a gesture.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    blocked.slice().forEach(function (video) {
      if (isOnScreen(video)) attempt(video);
    });
  });

  // The theme editor swaps section markup in and out without a page load, so newly
  // rendered videos have to be picked up. Cheaper and more predictable than a
  // MutationObserver over the whole document.
  document.addEventListener('shopify:section:load', function (event) {
    register(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { register(document); });
  } else {
    register(document);
  }

  window.HighThreadVideoAutoplay = { register: register, attempt: attempt };
})();
